import React, { useState, useEffect, useRef } from "react";
import Input from "../common/Input";
import Select from "../common/Select";
import Discount from "../common/Discount";
import add from "../assets/add.svg";
import { Redirect, NavLink } from "react-router-dom";
import BackArrow from "../common/BackArrow";
import API from "../common/Api";
import { connect } from "react-redux";
import { Shimmer } from "react-shimmer";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import DiscountDuration from "./DiscountDuration";
import DiscountedServices from "./DiscountedServices";

const mapStateToProps = (state) => ({
  headers: state.headers(),
  bookingSettings: state.bookingSettings,
  business: state.business,
});

function DiscountSettings({ headers, bookingSettings }) {
  const [addNewDiscount, setAddNewDiscount] = useState(true);
  const [editFormIsOpen, setEditFormIsOpen] = useState(false);
  const [addFormDefaults, setAddFormDefaults] = useState({
    code: "",
    type: "percentage",
    value: 0,
    status: true,
    title: "",
  });
  const [editFormDefaults, setEditFormDefaults] = useState({});
  const [discounts, setDiscounts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editingDiscount, setEditingDiscount] = useState(false);
  const [redirect, setRedirect] = useState(null);
  const [discountType, setDiscountType] = useState("code_discount");
  const [accountServiceCategories, setAccountServiceCategories] = useState([]);
  const [accountServices, setAccountServices] = useState([]);
  const [selectedServices, setSelectedServices] = useState([]);
  const [startDate, setStartDate] = useState(null);
  const [startTime, setStartTime] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [endTime, setEndTime] = useState(null);

  const types = [
    { name: "Percentage", value: "percentage" },
    { name: "Fixed Amount", value: "flat" },
  ];

  const addDiscountForm = useRef();
  const editDiscountForm = useRef();

  // Fetch discounts
  const getDiscounts = () => {
    API.get("business/discount-codes", { headers })
      .then(({ data }) => {
        setDiscounts(data.data);
        setIsLoading(false);
      })
      .catch(() => setIsLoading(false));
  };

  // Add discount
  const addServices = (ev) => {
    ev.preventDefault();
    const form = addDiscountForm.current;

    const payload = {
      type: form.type.value,
      value: Number(form.value.value),
      status: 1,
      discount_type: discountType,
    };

    payload.start_date = formatDate(startDate) || null;
    payload.start_time = formatTimeWithSeconds(startTime) || null;
    payload.end_date = formatDate(endDate) || null;
    payload.end_time = formatTimeWithSeconds(endTime) || null;

    if (discountType === "code_discount") {
      payload.code = form.code.value;
    } else if (discountType === "automatic_discount") {
      payload.title = form.title.value;
    }

    if (discountType === "automatic_discount") {
      payload.services = selectedServices;
    } else {
      payload.services = null;
    }

    API.post("business/discount-codes", payload, { headers })
      .then(({ data }) => {
        window.$.notify(data.message, "success-alert");
        form.reset();
        setAddNewDiscount(false);
        getDiscounts();
      })
      .catch((error) => {
        if (error.response && error.response.status === 400) {
          const msg =
            error.response.data.errors?.[0]?.message ||
            error.response.data.message ||
            "Bad Request";
          window.$.notify(msg, "error-alert");
        }
      });
  };

  // Edit discount
  const editDiscount = (ev) => {
    ev.preventDefault();
    setEditingDiscount(true);
    const form = editDiscountForm.current;

    const payload = {
      type: form.type.value,
      value: Number(form.value.value),
      discount_type: discountType,
    };

    if (discountType === "code_discount") {
      payload.code = form.code.value;
    } else if (discountType === "automatic_discount") {
      payload.title = form.title.value;
    }
    payload.start_date = formatDate(startDate) || null;
    payload.start_time = formatTimeWithSeconds(startTime) || null;
    payload.end_date = formatDate(endDate) || null;
    payload.end_time = formatTimeWithSeconds(endTime) || null;
    if (discountType === "automatic_discount") {
      payload.services = selectedServices;
    } else {
      payload.services = null;
    }

    API.put(`business/discount-codes/${editFormDefaults.id}`, payload, {
      headers,
    })
      .then(() => {
        window.$.notify("Discount updated successfully", "success-alert");
        setEditFormIsOpen(false);
        getDiscounts();
      })
      .catch((error) => {
        if (error.response && error.response.status === 400) {
          const msg =
            error.response.data.errors?.[0]?.message ||
            error.response.data.message ||
            "Bad Request";
          window.$.notify(msg, "error-alert");
        }
      })
      .finally(() => setEditingDiscount(false));
  };

  // Delete discount
  const deleteDiscount = (data) => {
    confirmAlert({
      title: "Confirm deletion",
      message: "Are you sure you want to permanently delete this discount?",
      buttons: [
        {
          label: "Yes, Delete",
          className: "btn btn-danger",
          onClick: () => {
            API.delete(`business/discount-codes/${data.id}`, { headers }).then(
              () => {
                window.$.notify(
                  "Discount deleted successfully",
                  "success-alert"
                );
                getDiscounts();
              }
            );
          },
        },
        { label: "No, Cancel" },
      ],
    });
  };

  const openEditDiscountForm = (data) => {
    const updatedData = {
      ...data,
      services: JSON.parse(data.services) ?? [],
      start_date: formatDateOnly(data.start_date) || null,
      start_time: normalizeTime(data.start_time, "toUI"),
      end_date: formatDateOnly(data.end_date) || null,
      end_time: normalizeTime(data.end_time, "toUI"),
    };
    setDiscountType(data.discount_type);
    setEditFormDefaults(updatedData);
    setEditFormIsOpen(true);
  };

  const closeEditDiscountForm = () => setEditFormIsOpen(false);

  useEffect(() => {
    getDiscounts();
    getServices();
    getCategories();
  }, []);

  useEffect(() => {
    if (discountType === "automatic_discount") {
      setAddFormDefaults((prev) => ({
        ...prev,
        type: "percentage",
        value: prev.type === "flat" && prev.value > 50 ? 0 : prev.value,
      }));
      setEditFormDefaults((prev) => ({
        ...prev,
        type: "percentage",
        value: prev.type === "flat" && prev.value > 50 ? 0 : prev.value,
      }));
    }
  }, [discountType]);

  // Discounted Services
  const handleSelect = (ids) => {
    setSelectedServices(ids);
  };


  const getServices = () => {
    API.get("business/services", { headers: headers }).then(({ data }) => {
      setAccountServices(data.data.services);
    });
  };

  const getCategories = () => {
    API.get("categories", { headers: headers }).then(({ data }) => {
      setAccountServiceCategories(data.data);
    });
  };

  /**
   * Normalizes time format between API (HH:mm:ss) and UI (HH:mm)
   */
  function normalizeTime(time, direction) {
    if (!time) return "";

    // Convert API -> UI (remove seconds)
    if (direction === "toUI") {
      // Example: "14:11:23" -> "14:11"
      return time.split(":").slice(0, 2).join(":");
    }

    // Convert UI -> API (add :00 if missing)
    if (direction === "toAPI") {
      const parts = time.split(":");
      if (parts.length === 2) {
        return `${time}:00`; // Example: "14:11" -> "14:11:00"
      }
      return time; // already has seconds
    }

    return time;
  }

  function formatTimeWithSeconds(time) {
    if (!time) return "";

    // If it's already a string like "14:25" or "14:25:39"
    if (typeof time === "string") {
      const parts = time.split(":");
      const hours = parts[0].padStart(2, "0");
      const minutes = parts[1] ? parts[1].padStart(2, "0") : "00";
      const seconds = parts[2] ? parts[2].padStart(2, "0") : "00";
      return `${hours}:${minutes}:${seconds}`;
    }

    // If it's a Date object
    const d = new Date(time);
    const hours = String(d.getHours()).padStart(2, "0");
    const minutes = String(d.getMinutes()).padStart(2, "0");
    const seconds = String(d.getSeconds()).padStart(2, "0");
    return `${hours}:${minutes}:${seconds}`;
  }

  function formatDate(date) {
    if (!date) return "";
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  function formatDateOnly(isoString) {
    if (!isoString) return "";
    const date = new Date(isoString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  return (
    <div className="onboarding-page app-setting">
      {redirect && <Redirect to={redirect} />}

      <div className="form_step form-page">
        <div className="header flex space-btw center">
          <div className="flex space-start center">
            {!addNewDiscount && !editFormIsOpen ? (
              <>
                <div className="discount-header">
                  <h1 className="form-title flex center">Discounts</h1>
                </div>
                {bookingSettings?.upfront_percentage_payment !== 100 && (
                  <div className="flex discountInfo">
                    <svg
                      style={{ marginRight: 5 }}
                      width="18"
                      height="18"
                      viewBox="0 0 18 18"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M9 15C10.5913 15 12.1174 14.3679 13.2426 13.2426C14.3679 12.1174 15 10.5913 15 9C15 7.4087 14.3679 5.88258 13.2426 4.75736C12.1174 3.63214 10.5913 3 9 3C7.4087 3 5.88258 3.63214 4.75736 4.75736C3.63214 5.88258 3 7.4087 3 9C3 10.5913 3.63214 12.1174 4.75736 13.2426C5.88258 14.3679 7.4087 15 9 15ZM9 16.5C4.85775 16.5 1.5 13.1422 1.5 9C1.5 4.85775 4.85775 1.5 9 1.5C13.1422 1.5 16.5 4.85775 16.5 9C16.5 13.1422 13.1422 16.5 9 16.5ZM8.25 8.25V12.75H9.75V8.25H8.25ZM8.25 5.25H9.75V6.75H8.25V5.25Z"
                        fill="#2A2B79"
                      />
                    </svg>
                    To use discount codes, change your required payment setting
                    to 100%.
                    <NavLink
                      to="/bookings/settings"
                      style={{ marginLeft: 20 }}
                      className="co-pink add"
                    >
                      Change setting
                    </NavLink>
                  </div>
                )}
              </>
            ) : (
              <h1 className="form-title mbl flex center">
                {addNewDiscount ? (
                  <>
                    <BackArrow backFunction={() => setAddNewDiscount(false)} />
                    Discounts
                  </>
                ) : (
                  <>
                    <BackArrow backFunction={closeEditDiscountForm} />
                    Discounts
                  </>
                )}
              </h1>
            )}
          </div>

          {!addNewDiscount && !editFormIsOpen && (
            <button
              className="co-pink add"
              onClick={() => setAddNewDiscount(true)}
            >
              <img src={add} alt="" />
              Create a Discount
            </button>
          )}
        </div>

        {/* Show all discounts */}
        {!addNewDiscount && !editFormIsOpen && (
          <div className="card-container">
            <br />
            <div className="form-container card services app-setting">
              {discounts.map((item) => (
                <Discount
                  key={item.id}
                  data={item}
                  editOnclick={openEditDiscountForm}
                  deleteOnclick={deleteDiscount}
                />
              ))}

              {!isLoading && discounts.length === 0 && (
                <p className="off">Discounts have not been added</p>
              )}

              {isLoading &&
                Array(6)
                  .fill(0)
                  .map((_, i) => (
                    <Shimmer key={i} className="r-shimer-service" height={91} />
                  ))}
            </div>
          </div>
        )}

        {/* Add Discount Form */}
        {addNewDiscount && (
          <div className="onboarding-page form-container card app-setting">
            <h1 className="form-title">Create a Discount</h1>
            <div className="discount-header">
              <div
                className="discount-switcher"
                style={{ width: "fit-content" }}
              >
                <button
                  type="button"
                  className={discountType === "code_discount" ? "active" : ""}
                  onClick={() => setDiscountType("code_discount")}
                >
                  Discount Code
                </button>
                <button
                  type="button"
                  className={
                    discountType === "automatic_discount" ? "active" : ""
                  }
                  onClick={() => setDiscountType("automatic_discount")}
                >
                  Automatic Discount
                </button>
              </div>
              <p className="discount_sub-title">
                {discountType === "automatic_discount"
                  ? "Automatically apply a discount to your services."
                  : "Let your clients get a discount by applying a code."}
              </p>

              <form
                onSubmit={addServices}
                ref={addDiscountForm}
                className="discount-feature-style"
              >
                <div style={{ maxWidth: "100%" }}>
                  <div className="discount-feature-content">
                    <div className="discount-feature-content-main">
                      {discountType === "code_discount" && (
                        <>
                          <Input
                            label="Code"
                            name="code"
                            placeholder="Enter discount code"
                            required
                            className="input-rapper withmini"
                          />
                          <div className="input_highlight">
                            <small>
                              Up to 8 characters. Letters and numbers only.
                            </small>
                          </div>
                        </>
                      )}

                      {discountType === "automatic_discount" && (
                        <>
                          <Input
                            label="Title"
                            name="title"
                            placeholder="Enter discount title"
                            required
                            className="input-rapper withmini"
                          />
                          <div className="input_highlight">
                            <small>
                              {" "}
                              Up to 20 characters. Customers will see this at
                              checkout.
                            </small>
                          </div>
                        </>
                      )}

                      <div className="flex relative">
                        <div style={{ width: "50%" }}>
                          <Select
                            name="type"
                            label="Type"
                            value={addFormDefaults.type}
                            onChange={(e) =>
                              setAddFormDefaults({
                                ...addFormDefaults,
                                type: e.target.value,
                              })
                            }
                            className="type_selector"
                            disabled={discountType === "automatic_discount"}
                          >
                            {types.map((t) => (
                              <option key={t.value} value={t.value}>
                                {t.name}
                              </option>
                            ))}
                          </Select>
                        </div>
                        <div
                          style={{ width: "50%" }}
                          className="spacer_left relative"
                        >
                          <Input
                            label="Value"
                            name="value"
                            type="number"
                            value={addFormDefaults.value}
                            onChange={(e) =>
                              setAddFormDefaults({
                                ...addFormDefaults,
                                value: e.target.value,
                              })
                            }
                            required
                            className="input-rapper padder discount-value-selector"
                          />
                          {addFormDefaults.type === "percentage" ? (
                            <span className="right signer">%</span>
                          ) : (
                            <span className="left signer">₦</span>
                          )}
                        </div>
                      </div>
                      <DiscountDuration
                        onChange={(data) => {
                          setStartDate(data.startDate);
                          setStartTime(data.startTime);
                          setEndDate(data.endDate);
                          setEndTime(data.endTime);
                        }}
                      />
                    </div>
                    {/* Discounted Services - for automatic discounts */}
                    {discountType === "automatic_discount" && (
                      <DiscountedServices
                        onSelect={handleSelect}
                        categories={accountServiceCategories}
                        services={accountServices}
                      />
                    )}
                  </div>

                  <div
                    className="flex center space-btw"
                    style={{ maxWidth: 400 }}
                  >
                    <button
                      type="button"
                      className="left-btn"
                      onClick={() => setAddNewDiscount(false)}
                    >
                      Back to discounts
                    </button>
                    <button className="default">Create Discount</button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Edit Discount Form */}
        {editFormIsOpen && (
          <div className="onboarding-page form-container card app-setting">
            <h1 className="form-title">Edit Discount</h1>
            <div className="discount-header">
              <div
                className="discount-switcher"
                style={{ width: "fit-content" }}
              >
                <button
                  type="button"
                  className={discountType === "code_discount" ? "active" : ""}
                  onClick={() => setDiscountType("code_discount")}
                >
                  Discount Code
                </button>
                <button
                  type="button"
                  className={
                    discountType === "automatic_discount" ? "active" : ""
                  }
                  onClick={() => setDiscountType("automatic_discount")}
                >
                  Automatic Discount
                </button>
              </div>
              <p className="discount_sub-title">
                {discountType === "automatic_discount"
                  ? "Automatically apply a discount to your services."
                  : "Let your clients get a discount by applying a code."}
              </p>
              <form
                onSubmit={editDiscount}
                ref={editDiscountForm}
                className="discount-feature-style"
              >
                <div style={{ maxWidth: "100%" }}>
                  <div className="discount-feature-content">
                    <div className="discount-feature-content-main">
                      {discountType === "code_discount" && (
                        <>
                          <Input
                            label="Code"
                            name="code"
                            placeholder="Enter discount code"
                            required
                            className="input-rapper withmini"
                            defaultValue={editFormDefaults.code}
                          />
                          <div className="input_highlight">
                            <small>
                              Up to 8 characters. Letters and numbers only.
                            </small>
                          </div>
                        </>
                      )}

                      {discountType === "automatic_discount" && (
                        <>
                          <Input
                            label="Title"
                            name="title"
                            placeholder="Enter discount title"
                            required
                            className="input-rapper withmini"
                            defaultValue={editFormDefaults.code}
                          />
                          <div className="input_highlight">
                            <small>
                              {" "}
                              Up to 20 characters. Customers will see this at
                              checkout.
                            </small>
                          </div>
                        </>
                      )}

                      <div className="flex relative">
                        <div style={{ width: "50%" }}>
                          <Select
                            name="type"
                            label="Type"
                            defaultValue={editFormDefaults.type}
                            value={editFormDefaults.type}
                            onChange={(e) =>
                              setEditFormDefaults({
                                ...editFormDefaults,
                                type: e.target.value,
                              })
                            }
                            className="type_selector"
                            disabled={discountType === "automatic_discount"}
                          >
                            {types.map((t) => (
                              <option key={t.value} value={t.value}>
                                {t.name}
                              </option>
                            ))}
                          </Select>
                        </div>
                        <div
                          style={{ width: "50%" }}
                          className="spacer_left relative"
                        >
                          <Input
                            label="Value"
                            name="value"
                            type="number"
                            defaultValue={editFormDefaults.value}
                            value={editFormDefaults.value}
                            onChange={(e) =>
                              setEditFormDefaults({
                                ...editFormDefaults,
                                value: e.target.value,
                              })
                            }
                            required
                            className="input-rapper padder discount-value-selector"
                          />
                          {editFormDefaults.type === "percentage" ? (
                            <span className="right signer">%</span>
                          ) : (
                            <span className="left signer">₦</span>
                          )}
                        </div>
                      </div>
                      <DiscountDuration
                        discountDuration={{
                          defaultStartDate: editFormDefaults.start_date,
                          defaultStartTime: editFormDefaults.start_time,
                          defaultEndDate: editFormDefaults.end_date,
                          defaultEndTime: editFormDefaults.end_time,
                        }}
                        onChange={(data) => {
                          setStartDate(data.startDate);
                          setStartTime(data.startTime);
                          setEndDate(data.endDate);
                          setEndTime(data.endTime);
                        }}
                      />
                    </div>
                    {/* Discounted Services - for automatic discounts */}
                    {discountType === "automatic_discount" && (
                      <DiscountedServices
                        onSelect={handleSelect}
                        categories={accountServiceCategories}
                        services={accountServices}
                        defaultServices={editFormDefaults.services}
                      />
                    )}
                  </div>

                  <div
                    className="flex center space-btw"
                    style={{ maxWidth: 400 }}
                  >
                    <button
                      type="button"
                      className="left-btn"
                      onClick={closeEditDiscountForm}
                    >
                      Back to discounts
                    </button>
                    <button
                      className={`default ${editingDiscount ? "loading" : ""}`}
                    >
                      Save
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default connect(mapStateToProps)(DiscountSettings);
